import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { canManageOrg } from "./lib/permissions";
import HomePage from "./pages/HomePage";
import SearchPage from "./pages/SearchPage";
import CategoryPage from "./pages/CategoryPage";
import CategoriesPage from "./pages/CategoriesPage";
import ItemDetailPage from "./pages/ItemDetailPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminItems from "./pages/AdminItems";
import AdminUsers from "./pages/AdminUsers";
import AdminLogs from "./pages/AdminLogs";
import AdminSettings from "./pages/AdminSettings";
import AdminRecycleBin from "./pages/AdminRecycleBin";
import LoginPage from "./pages/LoginPage";
import ClientUploadPage from "./pages/ClientUploadPage";
import MySubmissionsPage from "./pages/MySubmissionsPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminSubmissionsPage from "./pages/AdminSubmissionsPage";
import ProfilePage from "./pages/ProfilePage";
import AdminOrganizationsSummary from "./pages/AdminOrganizationsSummary";
import AdminItemSimilarityPage from "./pages/AdminItemSimilarityPage";
import AdminSubmissionSimilarityPage from "./pages/AdminSubmissionSimilarityPage";
import AdminPurchases from "./pages/AdminPurchases";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminImportHistory from "./pages/AdminImportHistory";
import AdminAuditPage from "./pages/AdminAuditPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import SmartSearchPage from "./pages/SmartSearchPage";
import MyCollectionsPage from "./pages/MyCollectionsPage";
import CollectionDetailPage from "./pages/CollectionDetailPage";
import ReferralPage from "./pages/ReferralPage";
import AboutPage from "./pages/AboutPage";
import TermsPage from "./pages/TermsPage";
import SeoHead from "./components/SeoHead";
import AuthCallback from "./components/AuthCallback";
import ResearchPage from "./pages/ResearchPage";
import SharedDraftPage from "./pages/SharedDraftPage";
import TopicsPage from "./pages/TopicsPage";
import TopicPage from "./pages/TopicPage";
import AdminTopics from "./pages/AdminTopics";
import AdminResearchAnalytics from "./pages/AdminResearchAnalytics";
// import SharedItemPreviewPage from "./pages/SharedItemPreviewPage";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">Loading...</div>;
  }

  // Emergent Google Auth: process session_id fragment BEFORE any routing.
  // Checked synchronously during render to avoid race conditions.
  if (window.location.hash?.includes("session_id=")) {
    return <AuthCallback setUser={setUser} />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <SeoHead />
        <Routes>
          <Route path="/" element={<HomePage user={user} setUser={setUser} />} />
          <Route path="/search" element={<SearchPage user={user} />} />
          <Route path="/category/:name" element={<CategoryPage user={user} />} />
          <Route path="/categories" element={<CategoriesPage user={user} />} />
          <Route path="/smart-search" element={<SmartSearchPage user={user} />} />
          <Route path="/research" element={<ResearchPage user={user} />} />
          <Route path="/research/shared/:token" element={<SharedDraftPage user={user} />} />
          <Route path="/topics" element={<TopicsPage user={user} />} />
          <Route path="/topics/:slug" element={<TopicPage user={user} />} />
          <Route path="/collections" element={<MyCollectionsPage user={user} />} />
          <Route path="/collections/:id" element={<CollectionDetailPage user={user} />} />
          <Route path="/referral" element={<ReferralPage user={user} />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route path="/items/:id" element={<ItemDetailPage user={user} />} />
          <Route path="/login" element={<LoginPage setUser={setUser} />} />
          <Route path="/upload" element={<ClientUploadPage user={user} />} />
          <Route path="/my-submissions" element={<MySubmissionsPage user={user} />} />
          <Route path="/profile" element={<ProfilePage user={user} setUser={setUser} />} />
          <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/items" element={user?.role === 'admin' ? <AdminItems user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/recycle-bin" element={user?.role === 'admin' ? <AdminRecycleBin user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/users" element={user?.role === 'admin' ? <AdminUsers user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/logs" element={user?.role === 'admin' ? <AdminLogs user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/settings" element={user?.role === 'admin' ? <AdminSettings user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/submissions" element={user?.role === 'admin' ? <AdminSubmissionsPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/submissions/:id/similarity" element={user?.role === 'admin' ? <AdminSubmissionSimilarityPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/organizations" element={user?.role === 'admin' ? <AdminOrganizationsSummary user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/items/:id/similarity" element={user?.role === 'admin' ? <AdminItemSimilarityPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/purchases" element={user?.role === 'admin' && canManageOrg(user) ? <AdminPurchases user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/subscriptions" element={user?.role === 'admin' ? <AdminSubscriptions user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/announcements" element={user?.role === 'admin' ? <AdminAnnouncements user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/imports" element={user?.role === 'admin' ? <AdminImportHistory user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/topics" element={user?.role === 'admin' ? <AdminTopics user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/research-analytics" element={user?.role === 'admin' ? <AdminResearchAnalytics user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/audit" element={user?.role === 'admin' ? <AdminAuditPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/subscriptions" element={<SubscriptionsPage user={user} setUser={setUser} />} />
          <Route path="/about" element={<AboutPage user={user} />} />
          <Route path="/terms" element={<TermsPage user={user} />} />
          {/* Share preview route temporarily disabled */}
          {/* <Route path="/share/:token" element={<SharedItemPreviewPage user={user} />} /> */}
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;